import LessonBookingRole from 'app/constants/LessonBookingRole';
import { IPollInterval } from 'app/services/rtc/bookings/behavior/types';
import { ONE_MINUTE_MS } from 'app/utils/date';

// Configuration for lesson booking role.
interface ILessonBookingRoleConfig {
  startOffsetMs: number;
  endOffsetMs: number;
  sleepMs: number;
}

// Shared configs by all LessonBookingRoles.
const SHARED_LESSON_BOOKING_ROLE_CONFIG: ILessonBookingRoleConfig[] = [
  // Captures if there are 2 bookings prepended to the multislot booking.
  // One tick between 70-75 minutes before the lesson.
  {
    startOffsetMs: ONE_MINUTE_MS * -75,
    endOffsetMs: ONE_MINUTE_MS * -70,
    sleepMs: ONE_MINUTE_MS * 5,
  },
  // Captures if there is 1 booking prepended to the multislot booking.
  // One tick between 40-45 minutes before the lesson.
  {
    startOffsetMs: ONE_MINUTE_MS * -45,
    endOffsetMs: ONE_MINUTE_MS * -40,
    sleepMs: ONE_MINUTE_MS * 5,
  },
];

// Configuration for each LessonBookingRole.
const LessonBookingRoleConfigs: {
  [LessonBookingRole.Student]: ILessonBookingRoleConfig[];
  [LessonBookingRole.Teacher]: ILessonBookingRoleConfig[];
} = {
  [LessonBookingRole.Student]: [
    ...SHARED_LESSON_BOOKING_ROLE_CONFIG,
    // Poll one time right before the lesson starts to update materials.
    // One tick within 1 minute of the lesson starts.
    {
      startOffsetMs: ONE_MINUTE_MS * -1,
      endOffsetMs: 0,
      sleepMs: ONE_MINUTE_MS,
    },
  ],
  [LessonBookingRole.Teacher]: [
    ...SHARED_LESSON_BOOKING_ROLE_CONFIG,
    // Poll multiple times before the lesson starts to capture potential cancellation events.
    // 15 ticks 15 minutes before the lesson starts.
    {
      startOffsetMs: ONE_MINUTE_MS * -15,
      endOffsetMs: 0,
      sleepMs: ONE_MINUTE_MS,
    },
  ],
};

/**
 * Creates polling intervals for lesson bookings.
 *
 * @param start Start of the lesson.
 * @param lessonBookingRole LessonBookingRole of the user.
 */
export const createPollingIntervals = (
  start: Date,
  lessonBookingRole: LessonBookingRole.Student | LessonBookingRole.Teacher,
): IPollInterval[] => {
  const config = LessonBookingRoleConfigs[lessonBookingRole];
  return config.map(({ startOffsetMs, endOffsetMs, sleepMs }) => ({
    start: new Date(start.valueOf() + startOffsetMs),
    end: new Date(start.valueOf() + endOffsetMs),
    sleepMs,
  }));
};
